import _ from 'lodash';
import { Badge } from 'reactstrap';
import {
  ConfirmModal,
  IconButton,
  WhiteDeleteIcon as whiteDeleteIcon,
  WhiteEyeIcon as whiteEyeIcon,
} from '@/shared/ui';
import { eventOffsetPreviewStrip } from '@/entities/strip';
import RhythmEventPreviewStrip from '@/features/event-strip-preview';
import useNewEvents from './useNewEvents';

export type NewEventsProps = {
  onCancel: () => void;
  timezoneOffset: number;
  studyId: string;
  profileId: string;
  startReport: any;
  ecgDataMap: any;
};

/**
 * Renders the draft list of newly created events as removable ECG thumbnails. Shows a red count badge and a no-event message when the list is empty, gives each strip overlay buttons to view or remove it, and routes removal through a confirm modal that remembers the pending index in a ref.
 *
 * @param props - Modal props with the ECG data map, timezone offset and report start.
 * @returns The rendered new-events modal body with its confirm-removal dialog.
 */
const NewEvents = ({
  onCancel,
  timezoneOffset,
  studyId,
  profileId,
  startReport,
  ecgDataMap,
}: NewEventsProps) => {
  const {
    keyRecoil,
    eventNews,
    activeButton,
    selectedStrip,
    isOpenConfirmRemove,
    setIsOpenConfirmRemove,
    stripRemoveIndex,
    channelsThumbnail,
    highPassThumbnail,
    gainThumbnail,
    onClickCancel,
    onClickRemoveStrips,
    onClickConfirmRemoveStrip,
    handleClickSelectEvent,
  } = useNewEvents({ onCancel, timezoneOffset, studyId, profileId });

  return (
    <>
      <ConfirmModal
        className="delete-instant-report-modal"
        isOpen={isOpenConfirmRemove}
        onClickLeftButton={() => {
          stripRemoveIndex.current = undefined;
          setIsOpenConfirmRemove(false);
        }}
        onClickRightButton={onClickConfirmRemoveStrip}
        leftButtonName="Cancel"
        rightButtonName="Remove"
        isRedBtn
        title="Remove artifact areas"
        question="This action cannot be undone. Are you sure you want to remove this are artifact areas?"
      />
      <div className="draggable-modal-content new-events">
        <div className="content-title">
          New events
          {eventNews.length > 0 && <Badge className="custom-badge-red">{eventNews.length}</Badge>}
        </div>
        <div className="content">
          {eventNews.length > 0 ? (
            _.map(eventNews, (holterEvent: any, index) => (
              <RhythmEventPreviewStrip
                id={`beats-new-event-${holterEvent.id}`}
                keyRecoil={keyRecoil}
                activeButton={activeButton}
                overlayContent={
                  <div className="overlay-single-strip">
                    <IconButton
                      id={`beats-new-event-delete-btn-${index}`}
                      isOutline
                      className="overlay-strip-btn"
                      iconComponent={<img src={whiteDeleteIcon} alt="Delete icon" />}
                      onClick={() => {
                        onClickRemoveStrips(index);
                      }}
                    />
                    <IconButton
                      id={`beats-new-event-view-btn-${index}`}
                      className="overlay-strip-btn ml3"
                      isOutline
                      iconComponent={<img src={whiteEyeIcon} alt="View icon" />}
                      onClick={() => {
                        handleClickSelectEvent(holterEvent);
                      }}
                    />
                  </div>
                }
                event={holterEvent}
                index={index}
                onClickSelectEvent={handleClickSelectEvent}
                isActive={holterEvent.id === selectedStrip?.id}
                ecgDataMap={ecgDataMap}
                width={296}
                height={67.3}
                highPass={highPassThumbnail}
                gain={gainThumbnail}
                channels={channelsThumbnail}
                customOffset={eventOffsetPreviewStrip}
                timezoneOffset={timezoneOffset}
                startReport={startReport}
              />
            ))
          ) : (
            <div className="no-event">There is no event</div>
          )}
        </div>
      </div>
      <div className="footer-btn">
        <button
          id="close-new-events"
          className="draggable-modal-btn single-btn"
          onClick={onClickCancel}
        >
          Close
        </button>
      </div>
    </>
  );
};

export default NewEvents;
